import { useCallback, useEffect, useRef, useState } from 'react';
import {
  advanceEddyFrame,
  EDDY_FRAME_HEIGHT_PCT,
  EDDY_FRAME_SEGMENT_MS,
  EDDY_FRAME_SPEED_PCT_PER_SEC,
  EDDY_FRAME_WIDTH_PCT,
  EDDY_LUCKY_SHOT_TIME_LIMIT_SEC,
  EDDY_MEME_HEIGHT_PCT,
  EDDY_MEME_WIDTH_PCT,
  EDDY_VICTORY_HOLD_MS,
  eddyFrameBounds,
  eddyRandomMemePosition,
  eddyVelocityForSegment,
  isEddyMemeInsideFrame,
  nextEddySegmentKind,
  type EddySegmentKind,
  type EddyVec2,
} from '../../../../domain';
import {
  EDDY_BUTTON_URL,
  EDDY_FRAME_URL,
  EDDY_TILE_URL,
  EDDY_TIMER_URL,
  EDDY_VICTORY_URL,
  pickEddyMemeUrl,
} from '../assets';
import './EddyLuckyShotGame.css';

export type EddyLuckyShotGameProps = {
  onWin: () => void;
  onLose: () => void;
  onTimerChange?: (secondsLeft: number) => void;
};

type Phase = 'play' | 'victory';

const FRAME_BOUNDS = eddyFrameBounds();

function formatEddyTimer(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} сек`;
}

export function EddyLuckyShotGame({
  onWin,
  onLose,
  onTimerChange,
}: EddyLuckyShotGameProps) {
  const [memePos] = useState<EddyVec2>(() => eddyRandomMemePosition());
  const [memeUrl] = useState(() => pickEddyMemeUrl());
  const [framePos, setFramePos] = useState<EddyVec2>(() => ({
    x: EDDY_FRAME_WIDTH_PCT / 2 + 4,
    y: EDDY_FRAME_HEIGHT_PCT / 2 + 4,
  }));
  const [secondsLeft, setSecondsLeft] = useState<number>(EDDY_LUCKY_SHOT_TIME_LIMIT_SEC);
  const [phase, setPhase] = useState<Phase>('play');
  const [finished, setFinished] = useState(false);

  const frameRef = useRef<EddyVec2>(framePos);
  const velRef = useRef<EddyVec2 | null>(null);
  if (velRef.current === null) {
    velRef.current = eddyVelocityForSegment('random', framePos, memePos, FRAME_BOUNDS);
  }
  const segmentKindRef = useRef<EddySegmentKind>('random');
  const segmentElapsedRef = useRef(0);

  useEffect(() => {
    onTimerChange?.(secondsLeft);
  }, [onTimerChange, secondsLeft]);

  useEffect(() => {
    if (phase !== 'play' || finished) {
      return;
    }
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finished, phase]);

  useEffect(() => {
    if (finished || phase !== 'play' || secondsLeft > 0) {
      return;
    }
    setFinished(true);
    onLose();
  }, [finished, onLose, phase, secondsLeft]);

  useEffect(() => {
    if (phase !== 'victory') {
      return;
    }
    const timer = window.setTimeout(() => {
      onWin();
    }, EDDY_VICTORY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [onWin, phase]);

  useEffect(() => {
    if (finished || phase !== 'play') {
      return;
    }
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      segmentElapsedRef.current += dt * 1000;
      if (segmentElapsedRef.current >= EDDY_FRAME_SEGMENT_MS) {
        segmentElapsedRef.current = 0;
        segmentKindRef.current = nextEddySegmentKind(segmentKindRef.current);
        velRef.current = eddyVelocityForSegment(
          segmentKindRef.current,
          frameRef.current,
          memePos,
          FRAME_BOUNDS,
        );
      }
      const advanced = advanceEddyFrame(
        frameRef.current,
        velRef.current!,
        dt,
        EDDY_FRAME_SPEED_PCT_PER_SEC,
        FRAME_BOUNDS,
        segmentKindRef.current,
        memePos,
      );
      frameRef.current = advanced.pos;
      velRef.current = advanced.vel;
      setFramePos(advanced.pos);
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [finished, memePos, phase]);

  const handleShoot = useCallback(() => {
    if (finished || phase !== 'play') {
      return;
    }
    const hit = isEddyMemeInsideFrame(
      {
        x: memePos.x,
        y: memePos.y,
        w: EDDY_MEME_WIDTH_PCT,
        h: EDDY_MEME_HEIGHT_PCT,
      },
      {
        x: frameRef.current.x,
        y: frameRef.current.y,
        w: EDDY_FRAME_WIDTH_PCT,
        h: EDDY_FRAME_HEIGHT_PCT,
      },
    );
    setFinished(true);
    if (hit) {
      setPhase('victory');
    } else {
      onLose();
    }
  }, [finished, memePos, onLose, phase]);

  return (
    <div className="eddy-shot">
      <img className="eddy-shot__tile" src={EDDY_TILE_URL} alt="" draggable={false} />
      <h2 className="eddy-shot__title">сними удачный кадр!</h2>
      <p className="eddy-shot__hint">Двигай рамку, найди мем и сними</p>
      <div
        className="eddy-shot__stage"
        onClick={handleShoot}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleShoot();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Снять кадр"
      >
        <img
          className="eddy-shot__meme"
          src={memeUrl}
          alt=""
          draggable={false}
          style={{
            left: `${memePos.x}%`,
            top: `${memePos.y}%`,
            width: `${EDDY_MEME_WIDTH_PCT}%`,
            height: `${EDDY_MEME_HEIGHT_PCT}%`,
          }}
        />
        <img
          className="eddy-shot__frame"
          src={EDDY_FRAME_URL}
          alt=""
          draggable={false}
          style={{
            left: `${framePos.x}%`,
            top: `${framePos.y}%`,
            width: `${EDDY_FRAME_WIDTH_PCT}%`,
            height: `${EDDY_FRAME_HEIGHT_PCT}%`,
          }}
        />
      </div>
      <button
        type="button"
        className="eddy-shot__shoot"
        onClick={handleShoot}
        disabled={finished || phase !== 'play'}
        aria-label="Снять"
      >
        <img className="eddy-shot__shoot-art" src={EDDY_BUTTON_URL} alt="" draggable={false} />
      </button>
      <div className="eddy-shot__timer">
        <img className="eddy-shot__timer-art" src={EDDY_TIMER_URL} alt="" draggable={false} />
        <p className="eddy-shot__timer-value">{formatEddyTimer(secondsLeft)}</p>
      </div>
      {phase === 'victory' ? (
        <div className="eddy-shot__victory" aria-live="polite">
          <img
            className="eddy-shot__victory-art"
            src={EDDY_VICTORY_URL}
            alt=""
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}
